import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Card, Col, List, Modal, Progress, Space, Spin, Tag, Typography } from "antd";
import { CheckCircleTwoTone, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Icons } from "../assets/icons";
import PageHeader from "../components/ui/PageHeader";
import SatageSubHeader from "../components/ui/SatageSubHeader";
import StepCard from "../components/ui/StepCard";
import { StepCardActions } from "../components/AwarnessSections/SectionOne.default";
import QuestionnaireAnswerModal from "../components/processes/QuestionnaireAnswerModal";
import RichText from "../components/ui/RichText";
import { useTokenInfo } from "../hooks/useTokenInfo";
import {
  getRoadmapStage,
  getRoadmapRequirements,
  getMyRoadmapProgress,
  getMyQuestionnaireAnswersForUser,
  getQuestionAnswersForAnswers,
} from "../repository/data.repository";
import {
  ICON_SIZE,
  RoadmapStage,
  RoadmapRequirement,
  RoadmapLevelAction,
  RoadmapLevelActionLabels,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";

const cardColors = ["#056159", "#1a1b4b", "#6082a3", "#7c3aed"];

// Per-linked-questionnaire state used to label the action (Start/Edit/View) + the last-updated hint.
interface AnswerState {
  readonly completed: boolean;
  readonly lastActivity?: string;
  // The version the answer was given on — reopened on that version even after a newer one is published.
  readonly questionnaireUid: string;
}

// A step and an answer meet on the questionnaire's lineage (its versions share it); the uid is the fallback.
const stepKey = (req: RoadmapRequirement): string =>
  req.assignableQuestionnaireLineageUid ?? req.assignableQuestionnaireUid ?? "";

function actionLabel(availableActions: string | null | undefined): string | null {
  return availableActions != null ? RoadmapLevelActionLabels[availableActions] ?? null : null;
}

function ProcessStagePage() {
  const { stageUid } = useParams<{ stageUid: string }>();
  const { userId } = useTokenInfo();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<RoadmapStage | null>(null);
  const [requirements, setRequirements] = useState<RoadmapRequirement[]>([]);
  const [currentStage, setCurrentStage] = useState(0);
  const [currentRequirement, setCurrentRequirement] = useState(0);
  const [answer, setAnswer] = useState<{ uid: string; name?: string | null } | null>(null);
  // My answer state per linked questionnaire — drives Start/Edit/View + the "last updated" hint.
  const [answerStates, setAnswerStates] = useState<Record<string, AnswerState>>({});

  const loadAnswers = useCallback(async () => {
    if (!userId) return;
    const mine = await getMyQuestionnaireAnswersForUser(userId).catch(() => null);
    const headers = mine?.data ?? [];
    if (!headers.length) {
      setAnswerStates({});
      return;
    }

    // One batch read of every child answer across my headers → the true latest edit per header
    // (the header's own updatedOn only changes on create/complete, not on per-question saves).
    const children = await getQuestionAnswersForAnswers(headers.map((h) => h.uid)).catch(() => null);
    const latestChildByHeader: Record<string, string> = {};
    for (const c of children?.data ?? []) {
      const ts = c.updatedOn ?? c.createdOn;
      if (!ts) continue;
      const prev = latestChildByHeader[c.questionnaireAnswerUid];
      if (!prev || ts > prev) latestChildByHeader[c.questionnaireAnswerUid] = ts;
    }

    const states: Record<string, AnswerState> = {};
    for (const h of headers) {
      // Max of the header timestamp and the latest child edit (ISO strings sort chronologically).
      const lastActivity = [h.updatedOn ?? h.createdOn, latestChildByHeader[h.uid]]
        .filter(Boolean)
        .sort()
        .pop() as string | undefined;
      // Keyed by the questionnaire's lineage: a step keeps its answer when a newer version is published, and
      // the answer stays on the version it was given on (questionnaireUid).
      states[h.questionnaireLineageUid ?? h.questionnaireUid] = {
        completed: h.completed,
        lastActivity,
        questionnaireUid: h.questionnaireUid,
      };
    }
    setAnswerStates(states);
  }, [userId]);

  const load = useCallback(async () => {
    if (!stageUid) return;
    setLoading(true);
    try {
      const [stageData, requirementsData] = await Promise.all([
        getRoadmapStage(stageUid),
        getRoadmapRequirements({
          searchQuery: {
            conditions: [
              { field: "roadmapLevelUid", values: [stageUid], operation: QueryOperation.Equal },
            ],
          },
          pageNumber: 0,
          pageSize: 1000,
          sortBy: "order",
          sortDescending: false,
        }),
      ]);
      setStage(stageData);
      setRequirements(requirementsData.data);

      const progress = await getMyRoadmapProgress(stageData.roadmapUid);
      setCurrentStage(progress?.currentStage ?? 0);
      setCurrentRequirement(progress?.currentRequirement ?? 0);

      await loadAnswers();
    } finally {
      setLoading(false);
    }
  }, [stageUid, loadAnswers]);

  useEffect(() => {
    load();
  }, [load]);

  // Step-by-step gating: earlier requirements are done, exactly one is the current open step,
  // later ones are locked — so managers progress in order and can't jump ahead.
  function reqState(order: number): "completed" | "current" | "locked" {
    if (!stage) return "locked";
    if (stage.order < currentStage) return "completed";
    if (stage.order > currentStage) return "locked";
    // same stage
    if (order < currentRequirement) return "completed";
    if (order === currentRequirement) return "current";
    return "locked";
  }

  function comingSoon(label: string, target?: string | null): void {
    Modal.info({
      title: `${label} — coming soon`,
      content: target
        ? `Distributing "${target}" to your employees will be available soon.`
        : "This action will be available soon.",
    });
  }

  // Actions depend on the requirement's gating state, its gated action, and the manager's answer state.
  // Managers complete requirements but never advance — an operator/admin reviews and advances the
  // company, which unlocks the next requirement here. So there is intentionally NO "Mark Done".
  //   no answer yet  → "Start"   · existing draft → "Edit"   · completed → "View" (read-only)
  function buildActions(req: RoadmapRequirement): StepCardActions[] {
    const state = reqState(req.order);
    if (state === "locked") return [];

    const actions: StepCardActions[] = [];
    const isAnswer =
      req.availableActions === RoadmapLevelAction.AnswerQuestionnaire &&
      !!req.assignableQuestionnaireUid;
    const isDistribute =
      req.availableActions === RoadmapLevelAction.DistributeBundle && !!req.assignableBundleUid;

    if (isAnswer) {
      const ans = answerStates[stepKey(req)];
      const name = !ans ? "Start" : ans.completed ? "View Report" : "Edit";
      actions.push({
        name,
        // An existing answer opens on ITS version; a new one starts on the step's current published version.
        action: () =>
          setAnswer({
            uid: ans?.questionnaireUid ?? req.assignableQuestionnaireUid!,
            name: req.assignableQuestionnaireName,
          }),
      });
    } else if (isDistribute && state === "current") {
      // Distribution to employees is a placeholder for now.
      actions.push({
        name: "Distribute",
        action: () => comingSoon("Distribute", req.assignableBundleName),
      });
    } else if (isDistribute && state === "completed") {
      // PD-02 §6.9.2.4 — once a bundle has been distributed the aggregated
      // report lives on the platform admin side (privacy/completion
      // threshold gating). From here we point the operator to request it.
      actions.push({
        name: "View Report",
        action: () =>
          Modal.info({
            title: "Aggregated report",
            content:
              "The bundle's aggregated report is published by your platform operator once the privacy/completion threshold is met. Please contact your operator if you have not received it.",
          }),
      });
    }
    // A misconfigured questionnaire step (no linked questionnaire) or a manual step shows no button.
    return actions;
  }

  // "Last updated …" hint shown under the action button — reflects the latest actual edit (max of the
  // answer header + its question-answers), computed client-side from the batch-loaded data.
  function requirementHint(req: RoadmapRequirement): string | undefined {
    if (req.availableActions !== RoadmapLevelAction.AnswerQuestionnaire || !req.assignableQuestionnaireUid)
      return undefined;
    const ts = answerStates[stepKey(req)]?.lastActivity;
    return ts ? `Last updated ${dayjs(ts).format("DD MMM YYYY, HH:mm")}` : undefined;
  }

  if (loading) {
    return (
      <div className="flex flex-auto items-center justify-center p-16">
        <Spin />
      </div>
    );
  }

  if (!stage) {
    return (
      <div className="flex flex-auto items-center justify-center p-16">
        <Typography.Text type="secondary">Stage not found.</Typography.Text>
      </div>
    );
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <PageHeader
        stage={stage.order}
        title={stage.name}
        icon={<Icons.AwarnessStageIcon width={ICON_SIZE} height={ICON_SIZE} />}
      />
      <SatageSubHeader
        overviewText={<RichText html={stage.description} />}
        stepsList={
          stage.stepsDescription ? (
            <RichText html={stage.stepsDescription} />
          ) : (
            <li>{stage.shortDescription}</li>
          )
        }
      />

      <Col className="p-6">
        {/* PD-02 §6.9.2.4 — Awareness Step 4 (and every other DistributeBundle
            step) surfaces: the aggregated assessment report is produced by
            the platform operator once completion + privacy thresholds are met,
            and shared back as a Wellbeing Report. Explain the flow once at the
            top of any stage that contains a bundle-distribution step. */}
        {requirements.some(
          (r) =>
            r.availableActions === RoadmapLevelAction.DistributeBundle &&
            !!r.assignableBundleUid,
        ) && (
          <Alert
            className="mb-6"
            type="info"
            showIcon
            message="How the bundle report becomes available"
            description={
              <ul className="m-0 list-disc pl-5">
                <li>Managers distribute the bundle to the chosen audience.</li>
                <li>Employees complete it on their own device.</li>
                <li>
                  Once the configured completion rate AND privacy threshold are
                  met, the platform operator publishes the aggregated report
                  back to this company — findable under Wellbeing Reports.
                </li>
                <li>
                  Partial-completion snapshots are not shared, protecting the
                  smallest-group privacy threshold.
                </li>
              </ul>
            }
          />
        )}
        {requirements.length === 0 && (
          <Typography.Text type="secondary">
            No requirements have been defined for this stage yet.
          </Typography.Text>
        )}
        {requirements.length > 0 &&
          stage.order === currentStage &&
          currentRequirement >= requirements.length && (
            <Alert
              className="mb-6"
              type="warning"
              showIcon
              message="Waiting for operator review"
              description="You've finished every step of this stage. The next stage will unlock once your operator (platform admin) reviews and advances the journey."
            />
          )}
        {requirements.length > 0 &&
          stage.order < currentStage &&
          requirements.every((r) => reqState(r.order) === "completed") && (
            <Alert
              className="mb-6"
              type="success"
              showIcon
              message="All steps in this stage are complete."
              description="You can revisit the answers here but the stage is locked for new changes."
            />
          )}
        {requirements.map((req, index) => {
          const action = actionLabel(req.availableActions);
          return (
            <StepCard
              key={req.uid}
              countValue={req.order}
              status={reqState(req.order)}
              title={req.name}
              checkListTitle={req.shortDescription || ""}
              backgroundColor={cardColors[index % cardColors.length]}
              actions={buildActions(req)}
              actionHint={requirementHint(req)}
              checkList={
                <>
                  {req.description && (
                    <li className="list-none">
                      <RichText html={req.description} />
                    </li>
                  )}
                  {req.stepsDescription && (
                    <li className="list-none">
                      <RichText html={req.stepsDescription} className="mt-2" />
                    </li>
                  )}
                  {(req.assignableQuestionnaireName || req.assignableBundleName || action) && (
                    <li className="list-none">
                      <Space size={[4, 4]} wrap className="mt-2">
                        {action && <Tag color="blue">{action}</Tag>}
                        {req.assignableQuestionnaireName && (
                          <Tag color="geekblue">Questionnaire: {req.assignableQuestionnaireName}</Tag>
                        )}
                        {req.assignableBundleName && (
                          <Tag color="purple">Bundle: {req.assignableBundleName}</Tag>
                        )}
                      </Space>
                    </li>
                  )}
                </>
              }
            />
          );
        })}
      </Col>

      {/* PD-02 §6.9 Integrated Summary — surfaces once the manager has
          completed every requirement of the stage. No AI; it just rolls up what
          was actually done so the operator (and the manager) have a terminal
          "what-did-we-do" view for sign-off. */}
      {requirements.length > 0 &&
        requirements.every((r) => reqState(r.order) === "completed") && (
          <StageIntegratedSummary
            stage={stage}
            requirements={requirements}
            answerStates={answerStates}
          />
        )}

      <QuestionnaireAnswerModal
        open={!!answer}
        questionnaireUid={answer?.uid}
        questionnaireName={answer?.name}
        onClose={() => {
          setAnswer(null);
          // Refresh answer states so the button (Start→Edit→View) + last-updated hint update.
          loadAnswers();
        }}
      />
    </Col>
  );
}

function StageIntegratedSummary(props: {
  readonly stage: RoadmapStage;
  readonly requirements: RoadmapRequirement[];
  readonly answerStates: Record<string, AnswerState>;
}) {
  const linkedCount = props.requirements.filter(
    (r) => r.availableActions === RoadmapLevelAction.AnswerQuestionnaire,
  ).length;
  const answered = props.requirements.filter((r) => {
    const key = stepKey(r);
    return key && props.answerStates[key]?.completed;
  }).length;
  const latest = Object.values(props.answerStates)
    .map((s) => s.lastActivity)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <Col className="px-6 pb-6">
      <Card
        className="emp-card"
        bodyStyle={{ padding: 20 }}
        title={
          <Space>
            <CheckCircleTwoTone twoToneColor="#14a098" />
            <span>{props.stage.name} — Integrated Summary</span>
          </Space>
        }
      >
        <Space direction="vertical" className="w-full" size={16}>
          <Space wrap>
            <Tag color="green">{props.requirements.length} steps</Tag>
            <Tag color="blue">
              {answered} / {linkedCount} assessments completed
            </Tag>
            {latest && (
              <Tag icon={<ClockCircleOutlined />} color="default">
                Last activity {dayjs(latest).format("DD MMM YYYY, HH:mm")}
              </Tag>
            )}
          </Space>
          <List
            size="small"
            dataSource={props.requirements}
            renderItem={(r) => {
              const key = stepKey(r);
              const ans = key ? props.answerStates[key] : undefined;
              const ts = ans?.lastActivity;
              return (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <CheckCircleTwoTone twoToneColor="#14a098" />
                    }
                    title={`${r.order}. ${r.name}`}
                    description={
                      <Space wrap>
                        {r.assignableQuestionnaireName && (
                          <Tag color="geekblue">
                            {r.assignableQuestionnaireName}
                          </Tag>
                        )}
                        {ts && (
                          <Typography.Text type="secondary">
                            {dayjs(ts).format("DD MMM YYYY, HH:mm")}
                          </Typography.Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
          <Typography.Text type="secondary">
            This summary is auto-generated from your completed steps. Submit the
            stage from Overview, or wait for your operator to advance the
            journey.
          </Typography.Text>
        </Space>
      </Card>
    </Col>
  );
}

export default ProcessStagePage;
